import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios, { AxiosResponse } from 'axios';
import topPic from '../icon/top_pic.jpg';
import Q1 from '../icon/Q1.jpg';
import Q2 from '../icon/Q2.jpg';
import Q3 from '../icon/Q3.jpg';
import Q4 from '../icon/Q4.jpg';
import Q5 from '../icon/Q5.jpg';
import Q6 from '../icon/Q6.jpg';
import Q7 from '../icon/Q7.jpg';
import Q8 from '../icon/Q8.jpg';
import Z1 from '../icon/Z1.png';
import Z2 from '../icon/Z2.png';
import Z3 from '../icon/Z3.png';
import Z4 from '../icon/Z4.png';
import { Link } from 'react-router-dom';
import ScrollToTop from '../components/ScrollToTop';

// 服务器地址配置
const SERVER_URL = 'http://47.238.48.112:5000';

interface StaffMember {
  id: number;
  name: string;
  name_en: string;
  position: string;
  position_en: string;
  experience: string;
  experience_en: string;
  photo_url: string;
}

interface Award {
  id: number;
  year: string;
  project: string;
  project_en: string;
  name: string;
  name_en: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

const About: React.FC = () => {
  const { i18n } = useTranslation();
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [awards, setAwards] = useState<Award[]>([]);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  useEffect(() => {
    // Fetch staff members from API
    axios.get<StaffMember[]>('/api/staff')
      .then((response: AxiosResponse<StaffMember[]>) => {
        setStaffMembers(response.data);
      })
      .catch((error: Error) => {
        console.error('Error fetching staff members:', error);
      });

    // Fetch awards from API
    axios.get<Award[]>('/api/awards')
      .then((response: AxiosResponse<Award[]>) => {
        setAwards(response.data);
      })
      .catch((error: Error) => {
        console.error('Error fetching awards:', error);
      });
  }, []);

  const getLocalizedText = (zh: string, en: string): string => {
    return i18n.language === 'en' ? en : zh;
  };

  const businessQualifications = [
    {
      id: 1,
      title: getLocalizedText('建筑设计', 'Architectural Design'),
      content: getLocalizedText(
        '我们提供全面的建筑设计服务，涵盖住宅、商业、办公和工业建筑。我们的团队拥有丰富的经验和国家级资质，确保每个项目的高标准和创新性设计。',
        'We offer comprehensive architectural design services, covering residential, commercial, office, and industrial buildings. Our team, with extensive experience and national-level qualifications, ensures high standards and innovative designs for every project.'
      ),
      image: Q1
    },
    {
      id: 2,
      title: getLocalizedText('规划设计', 'Urban Planning Design'),
      content: getLocalizedText(
        '我们的规划设计服务包括城市总体规划、区域规划和详细规划，致力于构建可持续发展的城市环境。凭借国家级资质，我们的方案兼具前瞻性和可行性。',
        'Our urban planning design services include master planning, regional planning, and detailed planning, aiming to create sustainable urban environments. With national-level qualifications, our plans are both forward-thinking and feasible.'
      ),
      image: Q2
    },
    {
      id: 3,
      title: getLocalizedText('室内设计', 'Interior Design'),
      content: getLocalizedText(
        '我们拥有国家认可的室内设计资质，可全过程和高质量交付室内设计项目。我们的设计不仅注重美学，更强调功能性和用户体验，确保每个空间都独一无二。',
        'We hold nationally recognized interior design qualifications and deliver high-quality interior design projects from concept to completion. Our designs prioritize aesthetics, functionality, and user experience, ensuring every space is unique.'
      ),
      image: Q3
    },
    {
      id: 4,
      title: getLocalizedText('景观设计', 'Landscape Design'),
      content: getLocalizedText(
        '我们的景观设计服务致力于打造美观且可持续的户外空间。无论是公共区域还是私人花园，我们的国家级资质团队都能提供卓越的设计和执行。',
        'Our landscape design services aim to create beautiful and sustainable outdoor spaces. Whether it\'s public areas or private gardens, our nationally qualified team delivers exceptional design and execution.'
      ),
      image: Q4
    },
    {
      id: 5,
      title: getLocalizedText('结构设计', 'Structural Design'),
      content: getLocalizedText(
        '我们为建筑提供全方位的结构解决方案，确保建筑的安全性和稳定性。我们的工程师团队拥有丰富的经验和专业知识，能够应对各种复杂的结构挑战。',
        'We provide comprehensive structural solutions to ensure the safety and stability of buildings. Our team of engineers possesses extensive experience and expertise, capable of tackling a wide range of complex structural challenges.'
      ),
      image: Q5
    },
    {
      id: 6,
      title: getLocalizedText('电气设计', 'Electrical Design'),
      content: getLocalizedText(
        '我们的电气设计服务涵盖电力系统规划、照明设计和智能控制系统，确保每个项目的高效和可靠性。',
        'Our electrical design services include power system planning, lighting design, and smart control systems, ensuring efficiency and reliability in every project.'
      ),
      image: Q6
    },
    {
      id: 7,
      title: getLocalizedText('暖通设计', 'HVAC Design'),
      content: getLocalizedText(
        '我们提供全面的暖通设计服务，确保室内环境的舒适性和节能性。无论是新建项目还是改造项目，我们都能提供最佳的解决方案。',
        'We offer comprehensive HVAC design services to ensure indoor comfort and energy efficiency. Whether it\'s a new construction or renovation project, we provide optimal solutions.'
      ),
      image: Q7
    },
    {
      id: 8,
      title: getLocalizedText('给排水设计', 'Plumbing and Drainage Design'),
      content: getLocalizedText(
        '我们的给排水设计服务涵盖生活用水、消防系统和废水处理，确保系统的高效性和可靠性。',
        'Our plumbing and drainage design services cover domestic water supply, fire protection systems, and wastewater treatment, ensuring efficiency and reliability.'
      ),
      image: Q8
    }
  ];

  return (
    <div className="pt-[80px] bg-gray-50 min-h-screen">
      {/* Back to top button */}
      <ScrollToTop />
      
      {/* Banner Section */}
      <div className="relative pt-[72px] overflow-hidden">
        <div className="w-full h-[250px] md:h-[630px]">
          <img 
            src={topPic} 
            alt={getLocalizedText('关于我们', 'About Us')} 
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Company Description Section with light gray background */}
      <div className="bg-[#f6f6f6] py-16">
        <div className="mx-auto w-full max-w-[2000px] px-4 md:px-12">
          <div className="flex flex-col">
            <div className="w-full">
              {/* 副标题 - 16px mobile, 24px desktop */}
              <p className="text-[16px] md:text-[24px] mb-4">{getLocalizedText('关于我们', 'About Us')}</p>
              
              {/* 主标题 - 36px mobile, 40px desktop */}
              <h1 className="text-[36px] md:text-[40px] font-normal mb-6">{getLocalizedText('深圳市东大国际工程设计有限公司', 'Shenzhen Dongda International Engineering Design Co., Ltd.')}</h1>
              
              {/* 0.5px separator line */}
              <div className="w-full h-[0.5px] bg-gray-300 mb-10"></div>
              
              {/* 正文介绍 - 14px mobile, 20px desktop */}
              <div className="text-[14px] md:text-[20px] text-gray-700 space-y-6 mb-12">
                <p>{getLocalizedText(
                  '深圳市东大国际工程设计有限公司依托东南大学， 已立足深圳三十余年，由"东南大学建筑设计研究院深圳分院"改制而来，创始院长为孟建民院士，是学习型、研究型、创新型的国家高新技术设计机构。东大国际拥有众多的技术人才，丰富的实践经验，严格的管理制度，完善的质保体系，现代化设计的甲级设计院，拥有在建筑界享有盛誉的"东大国际"品牌。',
                  'Shenzhen Dongda International Engineering Design Co., Ltd., relying on Southeast University, has been established in Shenzhen for over 30 years. It was transformed from the "Shenzhen Branch of Southeast University Architectural Design Institute," with Academician Meng Jianmin as its founding dean. It is a learning-oriented, research-oriented, and innovative national high-tech design institution. Dongda International has numerous technical talents, rich practical experience, strict management systems, and a comprehensive quality assurance system. As a modern Class-A design institute, it possesses the prestigious "Dongda International" brand in the architectural field.'
                )}</p>
                <p>{getLocalizedText(
                  '东大国际的专业设计团队致力向客户提供创新的建筑思维和全面的设计服务，并在办公综合、文化教育、酒店、产业园区、居住、室内设计、BIM设计上拥有广泛的设计及施工经验。公司代表作有北京人民大会堂福建厅和台湾厅 , 设计的福建武夷山庄、三亚金中海蓝钻、鲁能三亚湾美丽五区、南昌海昏侯国遗址公园展示中心等项目曾荣获国家级金质奖、教育部优秀设计一等奖。',
                  'The professional design team of Dongda International is committed to providing clients with innovative architectural thinking and comprehensive design services. The team has extensive design and construction experience in office complexes, cultural education, hotels, industrial parks, residential buildings, interior design, and BIM design. The company\'s representative works include the Fujian Hall and Taiwan Hall of the Great Hall of the People in Beijing. The designs of Fujian Wuyi Villa, Sanya Jinzhonghai Blue Diamond, Luneng Sanya Bay Beautiful Zone 5, and Nanchang Haihunhou State Archaeological Site Park Exhibition Center have won national gold awards and first-class awards for excellent design from the Ministry of Education.'
                )}</p>
              </div>
              
              {/* 替换原图片为视频播放器 - 使用逐步加载方式 */}
              <div className="h-[260px] md:h-[751px] relative">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  poster={topPic}
                  preload="auto"
                  playsInline
                  muted
                  autoPlay={false}
                  onLoadStart={(e) => {
                    // 视频开始加载
                    const target = e.target as HTMLVideoElement;
                    target.volume = 0.5; // 设置默认音量
                  }}
                  onCanPlay={(e) => {
                    // 当视频可以播放时（已加载足够数据）
                    const loadingIndicator = e.currentTarget.nextSibling as HTMLElement;
                    if (loadingIndicator) {
                      loadingIndicator.style.display = 'none';
                    }
                  }}
                >
                  {/* 使用 type 属性确保浏览器可以识别视频格式 */}
                  <source 
                    src={`${SERVER_URL}/uploads/aboutus.mp4`} 
                    type="video/mp4" 
                  />
                  {/* 添加多种格式源以提高兼容性 */}
                  <source 
                    src={`${SERVER_URL}/uploads/aboutus.webm`} 
                    type="video/webm" 
                  />
                  {getLocalizedText(
                    '您的浏览器不支持视频标签。',
                    'Your browser does not support the video tag.'
                  )}
                </video>
                {/* 加载指示器 */}
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50">
                  <div className="flex flex-col items-center text-gray-700">
                    <div className="w-12 h-12 border-4 border-gray-300 border-t-gray-700 rounded-full animate-spin mb-2"></div>
                    <div>{getLocalizedText('视频加载中...', 'Loading video...')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business and Qualification Section */}
      <div className="bg-white py-16">
        <div className="mx-auto w-full max-w-[2000px] px-4 md:px-12">
          <h2 className="text-[20px] md:text-[32px] text-gray-900 mb-4">
            {getLocalizedText('业务与资质', 'Business & Qualifications')}
          </h2>
          <div className="w-full h-[0.5px] bg-gray-300 mb-10"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {businessQualifications.map((item) => (
              <div 
                key={item.id}
                className="relative overflow-hidden shadow-md transition-all duration-300 aspect-square cursor-pointer"
                onMouseEnter={() => setHoveredCard(item.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-end p-6 transition-all duration-300">
                  <h3 className={`text-[20px] md:text-[24px] text-white mb-2 transition-transform duration-300 ${
                    hoveredCard === item.id ? 'transform -translate-y-4' : ''
                  }`}>
                    {item.title}
                  </h3>
                  <p className={`text-[12px] md:text-[16px] text-white transition-opacity duration-300 ${
                    hoveredCard === item.id ? 'opacity-100' : 'opacity-0 h-0'
                  }`}>
                    {item.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Staff Section */}
      <div className="bg-white py-16">
        <div className="mx-auto w-full max-w-[2000px] px-4 md:px-12">
          <h2 className="text-[20px] md:text-[32px] text-gray-900 mb-4">
            {getLocalizedText('人员', 'Staff')}
          </h2>
          <div className="w-full h-[0.5px] bg-gray-300 mb-10"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {staffMembers.slice(0, 8).map((staff) => (
              <div key={staff.id} className="bg-white overflow-hidden">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={staff.photo_url.startsWith('http') ? staff.photo_url : `${SERVER_URL}${staff.photo_url}`} 
                    alt={staff.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-[20px] font-medium mb-2">{i18n.language === 'en' ? staff.name_en : staff.name}</h3>
                  <p className="text-[16px] md:text-base text-gray-600 mb-2">{i18n.language === 'en' ? staff.position_en : staff.position}</p>
                  <p className="text-[12px] md:text-sm text-gray-500">{i18n.language === 'en' ? staff.experience_en : staff.experience}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex justify-center mt-12">
            <Link 
              to="/staff" 
              className="inline-flex items-center w-[165px] h-[54px] justify-center text-[16px] font-medium rounded-[4px] text-white bg-black hover:bg-gray-800 transition-colors duration-300"
            >
              {getLocalizedText('查看更多', 'View More')}
            </Link>
          </div>
        </div>
      </div>

      {/* Awards Section */}
      <div className="bg-white py-16">
        <div className="mx-auto w-full max-w-[2000px] px-4 md:px-12">
          <h2 className="text-[20px] md:text-[32px] text-gray-900 mb-4">
            {getLocalizedText('所获奖项', 'Awards')}
          </h2>
          <div className="w-full h-[0.5px] bg-gray-300 mb-10"></div>
          
          <div className="mb-[98px]">
            {awards
              .sort((a, b) => parseInt(b.year) - parseInt(a.year))
              .slice(0, 6)
              .map((award, index) => (
                <div key={award.id}>
                  {/* 移动端和PC端不同的布局 */}
                  {/* PC端布局 - 隐藏在移动端 */}
                  <div className="hidden md:flex items-center py-[34px]">
                    <div className="w-[200px] flex-shrink-0">
                      <div className="text-[24px] text-gray-900">{award.year}{i18n.language === 'zh' ? '年' : ''}</div>
                    </div>
                    <div className="flex-1 flex justify-between items-center">
                      <div className="w-[400px] text-[20px] text-gray-900 text-left">
                        {getLocalizedText(award.project, award.project_en)}
                      </div>
                      <div className="w-[400px] text-[20px] text-gray-600 text-left">
                        {getLocalizedText(award.name, award.name_en)}
                      </div>
                    </div>
                  </div>
                  
                  {/* 移动端布局 - 隐藏在PC端 */}
                  <div className="flex flex-col md:hidden py-[25px]">
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-[20px] text-gray-900 text-left max-w-[65%] pr-2">
                        {getLocalizedText(award.project, award.project_en)}
                      </div>
                      <div className="text-[16px] text-gray-500 text-right whitespace-nowrap">
                        {award.year}{i18n.language === 'zh' ? '年' : ''}
                      </div>
                    </div>
                    <div className="text-[12px] text-gray-600 text-left">
                      {getLocalizedText(award.name, award.name_en)}
                    </div>
                  </div>
                  
                  {index < 5 && (
                    <div className="w-full h-[0.5px] bg-gray-300"></div>
                  )}
                </div>
              ))}
          </div>

          <div className="flex justify-center">
            <Link 
              to="/awards" 
              className="inline-flex items-center w-[165px] h-[54px] justify-center text-[16px] font-medium rounded-[4px] text-white bg-black hover:bg-gray-800 transition-colors duration-300"
            >
              {getLocalizedText('查看更多', 'View More')}
            </Link>
          </div>
        </div>
      </div>

      {/* Technical Innovation Section (Placeholder) */}
      <div className="bg-[#f6f6f6] py-16">
        <div className="mx-auto w-full max-w-[2000px] px-4 md:px-12">
          <h2 className="text-[20px] md:text-[32px] text-gray-900 mb-4">
            {getLocalizedText('技术创新', 'Technical Innovation')}
          </h2>
          <div className="w-full h-[0.5px] bg-gray-300 mb-10"></div>
          
          {/* 四个技术创新卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* BIM */}
            <div className="flex flex-col">
              <div className="h-[211px] overflow-hidden bg-gray-200">
                <img 
                  src={Z1} 
                  alt="BIM" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-0">
                <h3 className="text-[24px] font-medium mb-2">BIM</h3>
                <p className="text-[12px] text-gray-600 mb-4">
                  {getLocalizedText(
                    '东大国际BIM技术部在BIM咨询、设计、施工管理、项目总协调等方面有多年深入的研究和大量BIM设计项目的实施应用，具有丰富的实践经验。众多BIM项目获得国家及部省市BIM设计应用奖项。',
                    'The BIM Technology Department of Dongda International has many years of in-depth research and extensive implementation experience in BIM consulting, design, construction management, and project coordination, with rich practical experience. Numerous BIM projects have won national, provincial, and municipal BIM design application awards.'
                  )}
                </p>
                <Link to="/bim" className="text-[16px] text-black underline cursor-pointer">
                  {getLocalizedText('查看详情', 'View Details')}
                </Link>
              </div>
            </div>
            
            {/* 装配式建筑 */}
            <div className="flex flex-col">
              <div className="h-[211px] overflow-hidden bg-gray-200">
                <img 
                  src={Z2} 
                  alt="装配式建筑" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-0">
                <h3 className="text-[24px] font-medium mb-2">{getLocalizedText('装配式建筑', 'Prefabricated Building')}</h3>
                <p className="text-[12px] text-gray-600 mb-4">
                  {getLocalizedText(
                    '东大国际长期致力于装配式建筑的研究与实践，聚焦建筑工业化领域各环节的创新技术研发，专注于装配式建筑的系统化发展，完善建筑工业化相关技术，专注于装配式建筑项目设计，项目涵盖办公、住宅、会展、工业园、学校等类型，完成装配式建筑面积达数百万平方米。',
                    'Dongda International has been committed to the research and practice of prefabricated buildings for a long time, focusing on the innovation and development of technologies in various aspects of building industrialization, the systematic development of prefabricated buildings, and improving related technologies. The company specializes in the design of prefabricated building projects, covering office buildings, residences, exhibition centers, industrial parks, schools, and other types, with completed prefabricated building area reaching millions of square meters.'
                  )}
                </p>
                <Link to="/prefab-building" className="text-[16px] text-black underline cursor-pointer">
                  {getLocalizedText('查看详情', 'View Details')}
                </Link>
              </div>
            </div>
            
            {/* 绿建 */}
            <div className="flex flex-col">
              <div className="h-[211px] overflow-hidden bg-gray-200">
                <img 
                  src={Z3} 
                  alt="绿建" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-0">
                <h3 className="text-[24px] font-medium mb-2">{getLocalizedText('绿建', 'Green Building')}</h3>
                <p className="text-[12px] text-gray-600 mb-4">
                  {getLocalizedText(
                    '东大国际着力于将建筑节能、绿色健康、海绵城市、超低能耗的绿色建筑理念引入项目全专业设计与项目全过程设计，从项目前期规划（绿建策划与咨询）到项目建筑设计与施工应用，获取标识认证等绿建全过程，完成了一系列绿色建筑设计项目。',
                    'Dongda International focuses on incorporating green building concepts such as building energy efficiency, green health, sponge city, and ultra-low energy consumption into comprehensive professional design and whole-process project design. From early project planning (green building planning and consulting) to architectural design and construction application, obtaining certification and the entire green building process, the company has completed a series of green building design projects.'
                  )}
                </p>
                <Link to="/green-building" className="text-[16px] text-black underline cursor-pointer">
                  {getLocalizedText('查看详情', 'View Details')}
                </Link>
              </div>
            </div>
            
            {/* 技术专利 */}
            <div className="flex flex-col">
              <div className="h-[211px] overflow-hidden bg-gray-200">
                <img 
                  src={Z4} 
                  alt="技术专利" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-0">
                <h3 className="text-[24px] font-medium mb-2">{getLocalizedText('技术专利', 'Technology Patents')}</h3>
                <p className="text-[12px] text-gray-600 mb-4">
                  {getLocalizedText(
                    '东大国际拥有多项技术专利，涵盖建筑结构、节能技术等多个领域，这些专利技术在实际项目中得到了广泛应用，提高了建筑品质和效率。',
                    'Dongda International holds multiple technology patents covering various fields such as building structures and energy-saving technologies. These patented technologies have been widely applied in practical projects, improving building quality and efficiency.'
                  )}
                </p>
                <Link to="/technology-patents" className="text-[16px] text-black underline cursor-pointer">
                  {getLocalizedText('查看详情', 'View Details')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About; 